import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, entityRenewal, assetDelinquent, liabilityDue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HouseholdContext = createContext(null);
const LS_KEY = "active_household_id";

export const HouseholdProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [households, setHouseholds] = useState([]);
  const [loadedFor, setLoadedFor] = useState(null);
  const [activeId, setActiveId] = useState(localStorage.getItem(LS_KEY) || null);
  const [entities, setEntities] = useState([]);
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);

  const reload = useCallback(async () => {
    if (!user) { setHouseholds([]); setLoadedFor(null); return; }
    try {
      const res = await api.get("/households");
      setHouseholds(res.data);
      setActiveId((cur) => (cur && res.data.some((h) => h.id === cur) ? cur : res.data[0]?.id || null));
    } catch (e) {
      setHouseholds([]);
    } finally {
      setLoadedFor(user.user_id);
    }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { if (activeId) localStorage.setItem(LS_KEY, activeId); }, [activeId]);

  const active = households.find((h) => h.id === activeId) || null;
  // Loading until auth resolves AND we've fetched households for the current user.
  const loading = authLoading || (!!user && loadedFor !== user.user_id);

  const refreshEntities = useCallback(async () => {
    if (!activeId) { setEntities([]); return; }
    try {
      const r = await api.get(`/entities?household_id=${activeId}`);
      setEntities(r.data);
    } catch (e) { setEntities([]); }
  }, [activeId]);
  useEffect(() => { refreshEntities(); }, [refreshEntities]);

  const refreshAssets = useCallback(async () => {
    if (!activeId) { setAssets([]); return; }
    try {
      const r = await api.get(`/assets?household_id=${activeId}`);
      setAssets(r.data);
    } catch (e) { setAssets([]); }
  }, [activeId]);
  useEffect(() => { refreshAssets(); }, [refreshAssets]);

  const refreshLiabilities = useCallback(async () => {
    if (!activeId) { setLiabilities([]); return; }
    try {
      const r = await api.get(`/liabilities?household_id=${activeId}`);
      setLiabilities(r.data);
    } catch (e) { setLiabilities([]); }
  }, [activeId]);
  useEffect(() => { refreshLiabilities(); }, [refreshLiabilities]);

  const liabilityDueIds = useMemo(
    () => new Set(liabilities.filter((l) => liabilityDue(l.due_date).due).map((l) => l.id)),
    [liabilities]
  );

  const flaggedEntityIds = useMemo(
    () => new Set(entities.filter((e) => entityRenewal(e.renewal_date, e.type).due).map((e) => e.id)),
    [entities]
  );
  const delinquentAssetIds = useMemo(
    () => new Set(assets.filter((a) => assetDelinquent(a)).map((a) => a.id)),
    [assets]
  );
  const leaseAlertAssetIds = useMemo(
    () => new Set(assets.filter((a) => a.lease_alert).map((a) => a.id)),
    [assets]
  );
  // A nav dot just means "this category has a problem" — combine tax + lease alerts.
  const assetProblemIds = useMemo(
    () => new Set([...delinquentAssetIds, ...leaseAlertAssetIds]),
    [delinquentAssetIds, leaseAlertAssetIds]
  );

  const ownerLabels = { A: "Operator A", B: "Operator B", joint: "Joint", ...(active?.owner_labels || {}) };
  const ownerLabel = (p) => ownerLabels[p] || p;
  // The owner_party (A/B) that maps to the currently signed-in user, when they're an operator.
  const myOwnerParty = active?.operator_label || "joint";

  return (
    <HouseholdContext.Provider value={{
      households, active, hid: active?.id || null, role: active?.role || null,
      isOperator: active?.role === "operator", loading, reload, setActiveId,
      ownerLabels, ownerLabel, myOwnerParty,
      entities, refreshEntities, flaggedEntityIds, hasEntityAlerts: flaggedEntityIds.size > 0,
      assets, refreshAssets, delinquentAssetIds, leaseAlertAssetIds, assetProblemIds,
      hasAssetAlerts: assetProblemIds.size > 0,
      liabilities, refreshLiabilities, liabilityDueIds, hasLiabilityAlerts: liabilityDueIds.size > 0,
    }}>
      {children}
    </HouseholdContext.Provider>
  );
};

export const useHousehold = () => useContext(HouseholdContext);
